/**
 * ViewAsUserManager - Sistema de gerenciamento e logging para o recurso "Visualizar como Usuário"
 * 
 * Este módulo gerencia o registro de todas as ações de "visualizar como usuário",
 * criando uma trilha de auditoria completa para fins de segurança e compliance.
 */

import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'viewAsUserLogs';

/**
 * Estrutura de um log:
 * {
 *   id: string (uuid),
 *   staffId: string,
 *   staffName: string,
 *   targetUserId: string,
 *   targetUserName: string,
 *   startTime: string (ISO timestamp),
 *   endTime: string | null (ISO timestamp),
 *   duration: number | null (seconds)
 * }
 */

/**
 * Obtém todos os logs armazenados
 * @returns {Array} Array de logs
 */
export const getViewAsUserLogs = () => {
    try {
        const logs = localStorage.getItem(STORAGE_KEY);
        return logs ? JSON.parse(logs) : [];
    } catch (error) {
        console.error('[ViewAsUser] Erro ao carregar logs:', error);
        return [];
    }
};

/**
 * Salva os logs no localStorage
 * @param {Array} logs - Array de logs para salvar
 */
const saveLogs = (logs) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
        console.error('[ViewAsUser] Erro ao salvar logs:', error);
    }
};

/**
 * Registra o início de uma sessão "visualizar como usuário"
 * @param {string} staffId - ID do staff que está visualizando
 * @param {string} staffName - Nome do staff
 * @param {string} targetUserId - ID do usuário sendo visualizado
 * @param {string} targetUserName - Nome do usuário sendo visualizado
 * @returns {string} ID do log criado
 */
export const logViewAsUserStart = (staffId, staffName, targetUserId, targetUserName) => {
    const logId = uuidv4();
    const newLog = {
        id: logId,
        staffId,
        staffName,
        targetUserId,
        targetUserName,
        startTime: new Date().toISOString(),
        endTime: null,
        duration: null
    };

    const logs = getViewAsUserLogs();
    logs.push(newLog);
    saveLogs(logs);

    // console.log(`[ViewAsUser] ${staffName} iniciou visualização como ${targetUserName}`);
    return logId;
};

/**
 * Registra o fim de uma sessão "visualizar como usuário"
 * @param {string} logId - ID do log a ser finalizado
 * @returns {boolean} Sucesso da operação
 */
export const logViewAsUserEnd = (logId) => {
    const logs = getViewAsUserLogs();
    const logIndex = logs.findIndex(log => log.id === logId);

    if (logIndex === -1) {
        console.error('[ViewAsUser] Log não encontrado:', logId);
        return false;
    }

    const log = logs[logIndex];
    const endTime = new Date();
    const startTime = new Date(log.startTime);
    const duration = Math.floor((endTime - startTime) / 1000); // duração em segundos

    logs[logIndex] = {
        ...log,
        endTime: endTime.toISOString(),
        duration
    };

    saveLogs(logs);
    // console.log(`[ViewAsUser] ${log.staffName} finalizou visualização como ${log.targetUserName} (${duration}s)`);
    return true;
};

/**
 * Obtém logs de um staff específico
 * @param {string} staffId - ID do staff
 * @returns {Array} Logs do staff
 */
export const getLogsByStaff = (staffId) => {
    const logs = getViewAsUserLogs();
    return logs.filter(log => log.staffId === staffId);
};

/**
 * Obtém logs de um usuário alvo específico
 * @param {string} userId - ID do usuário
 * @returns {Array} Logs do usuário
 */
export const getLogsByTarget = (userId) => {
    const logs = getViewAsUserLogs();
    return logs.filter(log => log.targetUserId === userId);
};

/**
 * Obtém estatísticas de uso do recurso
 * @returns {Object} Estatísticas
 */
export const getViewAsUserStats = () => {
    const logs = getViewAsUserLogs();

    const totalSessions = logs.length;
    const activeSessions = logs.filter(log => log.endTime === null).length;
    const completedSessions = logs.filter(log => log.endTime !== null).length;

    const avgDuration = completedSessions > 0
        ? logs
            .filter(log => log.duration !== null)
            .reduce((sum, log) => sum + log.duration, 0) / completedSessions
        : 0;

    const uniqueStaff = new Set(logs.map(log => log.staffId)).size;
    const uniqueTargets = new Set(logs.map(log => log.targetUserId)).size;

    return {
        totalSessions,
        activeSessions,
        completedSessions,
        avgDuration: Math.floor(avgDuration),
        uniqueStaff,
        uniqueTargets
    };
};

/**
 * Limpa logs antigos (mais de 90 dias)
 * @returns {number} Número de logs removidos
 */
export const cleanOldLogs = () => {
    const logs = getViewAsUserLogs();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const filteredLogs = logs.filter(log => {
        const logDate = new Date(log.startTime);
        return logDate > ninetyDaysAgo;
    });

    const removedCount = logs.length - filteredLogs.length;

    if (removedCount > 0) {
        saveLogs(filteredLogs);
        console.log(`[ViewAsUser] ${removedCount} logs antigos removidos`);
    }

    return removedCount;
};

/**
 * Exporta logs para JSON (para backup ou análise)
 * @returns {string} JSON string dos logs
 */
export const exportLogs = () => {
    const logs = getViewAsUserLogs();
    return JSON.stringify(logs, null, 2);
};

/**
 * Formata duração em formato legível
 * @param {number} seconds - Duração em segundos
 * @returns {string} Duração formatada
 */
export const formatDuration = (seconds) => {
    if (!seconds) return '0s';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(' ');
};
